import _ from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
// import { io } from 'socket.io-client'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useRpi } from '@/providers/rpi/RpiProvider'

// import { Consultation } from '@/core/models/consultation.model'

import { useAppSelector } from '@/redux/rootReducer'
import {
  saveMesures,
  selectConsultationById,
  selectConsultationCreateRequestTracker,
  selectConsultationUpdateRequestTracker,
  sendMesures,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'

import Mesures, { MesuresProps } from '../components/Mesures'
import { useElaraActions } from '@/providers/elara/hooks/useElaraActions'
import type { UIAction } from '@/providers/elara/types'

type RpiMesuresContainerProps = {
  consultationId?: string
  isStreamer?: boolean
}
export function RpiMesuresContainer(props: RpiMesuresContainerProps) {
  const { consultationId, isStreamer = false } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  // const [status, setPC300Status] = useState<boolean>(false)

  const consultation = useAppSelector(state => consultationId ? selectConsultationById(state, consultationId) : undefined)

  const mesures = useMemo(() => _.sortBy(consultation?.mesures, 'order'), [consultation])

  const { startMSV, stopMSV } = useRpi()

  useEffect(() => {
    if (!isStreamer) return

    startMSV()

    return () => {
      stopMSV()
    }
  }, [isStreamer, startMSV, stopMSV])

  const updateConsultationRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const saveMesuresRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.consultation.createMesuresCSV.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.consultation.createMesuresCSV.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const handleUpdateComment = (comment: string) => {
    if (consultationId) updateConsultationRequestHandler.dispatch(updateConsultation(consultationId, { commentaire: comment }))
  }

  const handleUpdateMesures = (mesures: MesuresProps['mesures']) => {
    if (consultationId) updateConsultationRequestHandler.dispatch(sendMesures(consultationId, mesures))
  }

  const handleSaveMesures = (csv: string) => {
    if (consultationId) saveMesuresRequestHandler.dispatch(saveMesures(consultationId, csv))
  }

  const elaraActions = useMemo((): UIAction[] => [
    {
      actionId: 'measurements.setValue',
      description: 'Set a vital sign measurement value (e.g., tension, saturation, temperature)',
      section: 'measurements',
      params: [
        { name: 'label', type: 'string' as const, description: 'Measurement label', required: true },
        { name: 'value', type: 'string' as const, description: 'Measurement value', required: true },
      ],
      handler: async (params) => {
        if (!params?.label || !params?.value) return { success: false, message: 'Missing label or value' }
        // Update via the existing handleUpdateMesures
        const updated = (mesures || []).map((m: any) =>
          m.label === params.label ? { ...m, value: params.value } : m
        )
        handleUpdateMesures(updated)
        return { success: true, message: `${params.label} set to ${params.value}` }
      },
    },
  ], [mesures, handleUpdateMesures])

  useElaraActions(elaraActions)

  return (
    <Mesures
      mesures={mesures}
      pcStatus={false} // TODO: msv status from rpi
      onMesuresChange={handleUpdateMesures}
      onMesuresSave={handleSaveMesures}
    />
  )
}

export default RpiMesuresContainer
